const MAX_HOST_LENGTH = 253;
const HOST_LABEL = /^[a-z0-9_-]{1,63}$/;

export interface SocialSite {
  key: string;
  label: string;
  domains: readonly string[];
}

/** Mirrors DataPlatform's ReferrerPlatform.All; filtering on a key also matches the platform's in-app browser. */
export const SOCIAL_SITES: readonly SocialSite[] = [
  {
    key: 'facebook',
    label: 'Facebook',
    domains: ['facebook.com', 'fb.me', 'com.facebook.katana', 'com.facebook.lite', 'com.facebook.android'],
  },
  { key: 'instagram', label: 'Instagram', domains: ['instagram.com', 'com.instagram.android', 'com.instagram.lite'] },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    domains: ['linkedin.com', 'lnkd.in', 'com.linkedin.android', 'com.linkedin.android.lite'],
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    domains: ['tiktok.com', 'com.zhiliaoapp.musically', 'com.ss.android.ugc.trill', 'com.ss.android.ugc.aweme'],
  },
  { key: 'youtube', label: 'YouTube', domains: ['youtube.com', 'youtu.be', 'com.google.android.youtube'] },
  {
    key: 'twitter',
    label: 'X (Twitter)',
    domains: ['x.com', 'twitter.com', 't.co', 'com.twitter.android', 'com.twitter.android.lite'],
  },
  { key: 'pinterest', label: 'Pinterest', domains: ['pinterest.com', 'pinterest.se', 'pin.it', 'com.pinterest'] },
  { key: 'snapchat', label: 'Snapchat', domains: ['snapchat.com', 'com.snapchat.android'] },
  { key: 'reddit', label: 'Reddit', domains: ['reddit.com', 'com.reddit.frontpage'] },
  { key: 'wechat', label: 'WeChat', domains: ['com.tencent.mm'] },
];

/** A typed site in stored form, or null; mirrors DataPlatform's ReferrerDomain.Normalize. */
export function normalizeReferrerDomain(text: string): string | null {
  let value = text.trim();
  if (value.startsWith('*.')) {
    value = value.slice(2);
  }

  let host: string;
  try {
    host = new URL(value.includes('://') ? value : `http://${value.replace(/^\/+/, '')}`).hostname;
  } catch {
    return null;
  }

  host = host.toLowerCase().replace(/\.$/, '');
  if (host.startsWith('www.')) {
    host = host.slice(4);
  }
  return host.length > 0 && host.length <= MAX_HOST_LENGTH && host.split('.').every((label) => HOST_LABEL.test(label))
    ? host
    : null;
}

export function socialSiteOf(host: string): SocialSite | undefined {
  return SOCIAL_SITES.find((site) => site.domains.some((domain) => host === domain || host.endsWith(`.${domain}`)));
}

/** A platform's key or any of its sites becomes the key; anything else its stored host. */
export function parseReferrer(text: string): string | null {
  const key = text.trim().toLowerCase();
  if (SOCIAL_SITES.some((site) => site.key === key)) {
    return key;
  }
  const host = normalizeReferrerDomain(text);
  return host ? (socialSiteOf(host)?.key ?? host) : null;
}
